import React, { useState, useEffect } from 'react';
import { Modal, Box, Button } from '@mui/material';

import { QuestionRenderer, type QtiQuestionDTO } from '@/src/lib';
import { apiRequest } from '@/src/services';
import { ErrorMessage, Loading } from '@/src/ui';

interface PreviewModalProps {
    open: boolean;
    resourceId: number | undefined;
    handleClose: (_shouldRefresh: boolean, _isInTimeout: boolean) => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ open, resourceId, handleClose }) => {
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState('');

    const [data, setData] = useState<QtiQuestionDTO | null>(null);

    useEffect(() => {
        (async function init() {
            let res = null;
            try {
                res = await apiRequest<QtiQuestionDTO>(
                    'GET',
                    `assessments/questions/${resourceId}`
                );
            } catch {
                setError('Failed to load question data');
                return;
            } finally {
                setHasLoaded(true);
            }

            setData(res.data);
        })();
    }, [resourceId]);

    return (
        <Modal open={open} onClose={() => handleClose(false, false)} className="user-min-font-size">
            <Box
                sx={{
                    p: 4,
                    bgcolor: 'background.paper',
                    margin: 'auto',
                    mt: 8,
                    width: 1000,
                    height: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                }}
            >
                <h2>Question Preview</h2>

                <br />

                <div className="font-bold">
                    Question Title: {data?.title || 'Untitled Question'}
                </div>

                <div className="border rounded p-4 bg-white max-w-4xl space-y-4 mt-4">
                    {!hasLoaded ? (
                        <Loading message="Loading..." size="lg" />
                    ) : data ? (
                        <QuestionRenderer
                            question={data}
                            stimMode="preview"
                            options={{
                                showScoring: true,
                                showAudioButtons: true,
                            }}
                        />
                    ) : (
                        <p>Unable to load question.</p>
                    )}
                </div>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    <Button variant="outlined" onClick={() => handleClose(false, false)}>
                        Close
                    </Button>
                </Box>
                <ErrorMessage error={error} style={{ marginTop: 8 }} />
            </Box>
        </Modal>
    );
};

export default PreviewModal;
